import React, { useState, useCallback, useRef, useEffect } from "react";
import closeBtn from "../../../assets/design-revamp-company/company-epr/x_btn_epr.svg";
import "./index.css";
import {
  getProductImages,
  deleteProductImages,
  addProductImages,
} from "../../../apis/Product/ProductImagesAPi";
import productUpload from "../../../assets/design-revamp-product/product-icons/upload-product-image.svg";
import { createSafeUploadWidget } from "../../../components/CloudinaryUploadWidget/ensureCloudinary";

const ProductImages = ({ productId }) => {
  // Changed state structure to use objects with IDs instead of arrays
  const [images, setImages] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  const widgetRef = useRef();
  const imagesCountRef = useRef(0);

  const cloudName = "azilisdjo";
  const uploadPreset = "upload";
  const folder = "productimages";

  useEffect(() => {
    imagesCountRef.current = images.length;
  }, [images]);

  useEffect(() => {
    const handle = createSafeUploadWidget(
      {
        cloudName: cloudName,
        uploadPreset: uploadPreset,
        folder: folder,
      },
      async function (error, result) {
        if (!error && result && result.event === "success") {
          setIsLoading(true);
          try {
            const imageUrl = result.info.secure_url;
            const imagePublicId = result.info.public_id;

            if (imagesCountRef.current >= 3) {
              console.warn("Maximum number of images reached");
              return;
            }

            await addProductImages({
              productId: productId,
              image: imageUrl,
              publicId: imagePublicId,
            });

            setImages((prevImages) => [
              ...prevImages,
              {
                objectId: result.info.asset_id,
                image: imageUrl,
                publicId: imagePublicId,
                fileName: result.info.original_filename,
              },
            ]);
          } catch (error) {
            console.error("Error saving image:", error);
          } finally {
            setIsLoading(false);
          }
        }
      }
    );
    widgetRef.current = handle;
    return () => handle.destroy();
  }, [productId]);

  const handleUpload = useCallback(() => {
    if (images.length >= 3) {
      return;
    }
    widgetRef.current?.open();
  }, [images]);

  const handleClear = useCallback(
    async (objectId, e) => {
      setIsLoading(true);
      e.stopPropagation();
      try {
        await deleteProductImages({
          productId: productId,
          objectId: objectId,
        });

        setImages((prevImages) =>
          prevImages.filter((img) => img.id !== objectId)
        );
      } catch (error) {
        console.error("Error clearing image:", error);
      } finally {
        setIsLoading(false);
      }
    },
    [productId]
  );

  useEffect(() => {
    const loadInitialImages = async () => {
      setIsLoading(true);
      try {
        const response = await getProductImages({ productId: productId });
        if (response?.data?.images) {
          setImages(response.data.images);
        }
      } catch (error) {
        console.error("Error loading initial images:", error);
      } finally {
        setIsLoading(false);
      }
    };

    loadInitialImages();
  }, [productId]);

  const ImageUploadBox = ({ image, index }) => (
    <div className="relative">
      {image && (
        <img
          src={closeBtn}
          alt="Close btn"
          width={16}
          height={16}
          className="absolute -top-1 -left-1.5 cursor-pointer"
          style={{
            cursor: isLoading ? "not-allowed" : "pointer",
            opacity: isLoading ? 0.5 : 1,
            position: "absolute",
            zIndex: "5",
          }}
          onClick={
            !isLoading ? (event) => handleClear(image.id, event) : undefined
          }
        />
      )}
      <div
        onClick={!isLoading && !image ? handleUpload : undefined}
        style={{
          cursor: isLoading ? "not-allowed" : "pointer",
          opacity: isLoading ? 0.5 : 1,
          width: "161px",
          height: index === 0 ? "230px" : "105px",
          overflow: "hidden",
          display: "flex",
          justifyContent: "center",
        }}
      >
        {image ? (
          <img
            src={image.image}
            alt="Product"
            style={{
              width: "100%",
              aspectRatio: index === 0 ? "161/230" : "161/105",
              overflow: "hidden",
              display: "flex",
              justifyContent: "center",
              objectFit: "cover",
            }}
            className={`${index === 0 && "existing-product-img"}`}
          />
        ) : (
          <img src={productUpload} alt="Upload Icon" width={161} height={105} />
        )}
      </div>
    </div>
  );

  // Create placeholder array for rendering
  const displayImages = [...images];
  while (displayImages.length < 3) {
    displayImages.push(null);
  }

  return (
    <div style={{ display: "flex", gap: "12px", marginLeft: "24px" }}>
      <div>
        <ImageUploadBox image={displayImages[0]} index={0} />
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: "17px" }}>
        <ImageUploadBox image={displayImages[1]} index={1} />
        <ImageUploadBox image={displayImages[2]} index={2} />
      </div>
    </div>
  );
};

export default ProductImages;
